import * as Sentry from "@sentry/react";
import { QueryCache, QueryClient, MutationCache, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "react-oidc-context";
import { BrowserRouter } from "react-router";
import { SharedApiProvider } from "@repo/shared-components";
import { DialogProvider, ToastProvider, toastEmitter } from "@repo/ui";
import { configurationApi, fileApi } from "./api";
import { App } from "./app";
import { oidcConfig } from "./auth-config";
import { AuthErrorHandler } from "./components/auth/auth-error-handler";
import { AuthTokenUpdater } from "./components/auth/auth-token-updater";
import { getErrorMessage } from "./hooks/helpers";
import { WorkflowCreationContextProvider } from "./pages/administration/workflows-page/workflow-creation-context-provider";
import { ConfigurationSignalRProvider } from "./providers/configuration-signal-r-provider";
import { FileSignalRProvider } from "./providers/file-signal-r-provider";

Sentry.init({
  dsn: import.meta.env.VITE_PUBLIC_SENTRY_DSN,
  environment: import.meta.env.VITE_APP_ENV ?? "local",
  release: import.meta.env.VITE_SENTRY_RELEASE,
  sendDefaultPii: true,
  // eslint-disable-next-line turbo/no-undeclared-env-vars
  enabled: !import.meta.env.DEV && import.meta.env.VITE_APP_ENV !== "development",
});

// Sentry.captureMessage("Sentry in dashboard is initialized!");

// Catch unhandled promise rejections globally (skip API/network errors)
window.addEventListener("unhandledrejection", (event) => {
  const error = event.reason;
  console.error("Unhandled promise rejection:", error);

  const isApiError =
    error?.name === "AbortError" ||
    error?.message?.includes("fetch") ||
    error?.message?.includes("NetworkError") ||
    error?.status !== undefined;

  if (!isApiError) {
    Sentry.captureException(error);
  }
  event.preventDefault();
});

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Ignore aborted requests (React StrictMode double-mount, navigation, unmount)
      if (error instanceof DOMException && error.name === "AbortError") return;

      // Uncomment if you want to log all query errors to Sentry
      Sentry.captureException(error, {
        extra: { queryKey: JSON.stringify(query.queryKey) },
      });

      // Skip toast if query opts out of global error handling
      if (query.meta?.skipGlobalErrorHandler) {
        console.warn("[Query Error - Global Handler Skipped]:", error);
        return;
      }

      const title = (query.meta?.errorTitle as string) ?? "Error";
      const message = getErrorMessage(error, "Something went wrong");

      toastEmitter.emit("error", message, title);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Uncomment if you want to log all query errors to Sentry
      Sentry.captureException(error, {
        extra: { mutationKey: JSON.stringify(mutation.options.mutationKey) },
      });

      // Mutations handle their own errors via onError callbacks, so only log here
      if (!mutation.meta?.skipGlobalErrorHandler) {
        console.error("[Mutation Error]:", error);
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const rootElement = document.getElementById("root");

if (rootElement) {
  createRoot(rootElement, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  }).render(
    <StrictMode>
      <ToastProvider>
        <QueryClientProvider client={queryClient}>
          <SharedApiProvider configurationApi={configurationApi} fileApi={fileApi}>
            <ConfigurationSignalRProvider>
              <FileSignalRProvider>
                <AuthProvider {...oidcConfig}>
                  <BrowserRouter>
                    <WorkflowCreationContextProvider>
                      <DialogProvider>
                        <AuthTokenUpdater />
                        <AuthErrorHandler />
                        <App />
                      </DialogProvider>
                    </WorkflowCreationContextProvider>
                  </BrowserRouter>
                </AuthProvider>
              </FileSignalRProvider>
            </ConfigurationSignalRProvider>
          </SharedApiProvider>
        </QueryClientProvider>
      </ToastProvider>
    </StrictMode>
  );
}
